/**
 * Contract Preview Component
 *
 * Displays a formatted contract preview with dynamic data from LO Data form
 */

import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Loader2, FileText, Download, Edit } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { supabase } from '@/utils/supabase';
import type { RecruitmentFormValues } from './validations';
import type { ContractVariableData, ContractSection } from './contract-types';
import { useContract } from './useContractTemplate';
import { useContractInstance } from '@/hooks/useRecruitment';
import { interpolateVariables, computeDerivedVariables } from './contract-utils';
import { SectionRenderer } from './ContractSectionRenderers';
import { createDefaultContractTemplate } from './contract-seed-template';
import { ContractTemplateEditor } from './ContractTemplateEditor';

interface ContractPreviewProps {
  form: UseFormReturn<RecruitmentFormValues>;
  candidateId?: string;
}

export const ContractPreview: React.FC<ContractPreviewProps> = ({ form, candidateId }) => {
  const { watch } = form;
  const formData = watch();

  // localStorage-based hook (for template editing and checkbox states)
  const {
    template,
    templateLoading,
    templateError,
    instance: localInstance,
    instanceLoading: localInstanceLoading,
    saveTemplate
  } = useContract(candidateId);

  // Supabase-based hook (for actual contract instance with auto-regenerated sections)
  const {
    instance: dbInstance,
    loading: dbInstanceLoading,
    updateCheckboxStates: updateCheckboxStatesDb
  } = useContractInstance(candidateId);

  const [isInitializing, setIsInitializing] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [mentorName, setMentorName] = useState<string>('');

  // Initialize template if it doesn't exist
  useEffect(() => {
    if (!templateLoading && !template && !templateError && !isInitializing) {
      setIsInitializing(true);
      const defaultTemplate = createDefaultContractTemplate();
      saveTemplate(defaultTemplate);
      setIsInitializing(false);
    }
  }, [template, templateLoading, templateError, saveTemplate, isInitializing]);

  // Fetch mentor name when mentorId changes
  useEffect(() => {
    const fetchMentorName = async () => {
      if (!formData.mentorId) {
        setMentorName('');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('full_name, first_name, last_name')
          .eq('id', formData.mentorId)
          .single();

        if (error) {
throw error;
}

        // Prioritize full_name, then construct from first/last, fallback to individual names
        const name = data.full_name ||
                     (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : '') ||
                     data.first_name ||
                     data.last_name ||
                     '';

        setMentorName(name);
      } catch (error) {
        console.error('Error fetching mentor name:', error);
        setMentorName('');
      }
    };

    fetchMentorName();
  }, [formData.mentorId]);

  // Convert form data to variable data
  const variableData: ContractVariableData = useMemo(() => {
    const baseData: ContractVariableData = {
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      preferredFirstName: formData.preferredFirstName || '',
      email: formData.email || '',
      phone: formData.phone || '',
      dateOfBirth: formData.dateOfBirth || '',
      mailingStreetAddress: formData.mailingStreetAddress || '',
      mailingCity: formData.mailingCity || '',
      mailingState: formData.mailingState || '',
      mailingZip: formData.mailingZip || '',
      nmlsNumber: formData.nmlsNumber || '',
      dreNumber: formData.dreNumber || '',
      yearsOfExperience: formData.yearsOfExperience ?? 0,
      stateLicenses: formData.stateLicenses || [],
      bankedCompensation: formData.bankedCompensation,
      brokerCommissionSplit: formData.brokerCommissionSplit,
      bankedOverrideBps: formData.bankedOverrideBps,
      overrideRecipientName: '', // Not needed in contracts (accounting only)
      brokerMentorCommissionSplit: formData.brokerMentorCommissionSplit,
      maximumCompensation: formData.maximumCompensation, // Maximum commission cap
      mentorName,
      position: formData.position || 'Loan Officer',
      contractDate: undefined, // Will be set when contract is actually signed
      companyName: 'Lendwise Financial',
      companyAddress: '123 Main Street, Los Angeles, CA 90001'
    };

    return computeDerivedVariables(baseData);
  }, [formData, mentorName]);

  // Checkbox states from database instance or local instance or empty object
  const checkboxStates = dbInstance?.checkboxStates || localInstance?.checkboxStates || {};

  // Handle checkbox changes - persist to database
  const handleCheckboxChange = async (checkboxId: string, checked: boolean) => {
    if (!candidateId || !dbInstance) {
return;
}

    const updatedStates = {
      ...checkboxStates,
      [checkboxId]: checked
    };

    // Use database-backed function for persistence
    await updateCheckboxStatesDb(updatedStates);
  };

  // Interpolation function
  const interpolate = (text: string, variables: ContractVariableData) => {
    return interpolateVariables(text, variables, { fallback: '[Not provided]' });
  };

  // Handle PDF export
  const handleExportPDF = () => {
    // TODO: Implement PDF export
  };

  // Loading state
  if (templateLoading || localInstanceLoading || dbInstanceLoading || isInitializing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 className="animate-spin" style={{ width: 32, height: 32, color: 'var(--lw-muted)', margin: '0 auto 16px', display: 'block' }} />
          <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>Loading contract template...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (templateError) {
    return (
      <div style={{ background: '#F6EBE5', border: '1px solid #E3C4B6', borderRadius: 11, padding: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7E3B27', margin: '0 0 8px' }}>Error Loading Template</h3>
        <p style={{ fontSize: 13, color: '#8A5A47', margin: 0 }}>{templateError}</p>
        <button
          type="button"
          className="btn-sm"
          style={{ marginTop: 16 }}
          onClick={() => {
            const defaultTemplate = createDefaultContractTemplate();
            saveTemplate(defaultTemplate);
          }}
        >
          Reset to Default Template
        </button>
      </div>
    );
  }

  // No template state
  if (!template) {
    return (
      <div className="panel" style={{ padding: 24, textAlign: 'center' }}>
        <FileText size={48} style={{ color: 'var(--lw-muted)', margin: '0 auto 16px', display: 'block' }} />
        <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--green-900)', margin: '0 0 8px' }}>No Contract Template</h3>
        <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: '0 0 16px' }}>
          No contract template found. Click below to create the default template.
        </p>
        <button
          type="button"
          className="btn-sm solid"
          onClick={() => {
            const defaultTemplate = createDefaultContractTemplate();
            saveTemplate(defaultTemplate);
          }}
        >
          Create Default Template
        </button>
      </div>
    );
  }

  // Use database instance sections if available, otherwise fall back to localStorage template
  // Database instance sections are auto-regenerated from latest template for unsigned contracts
  const sectionsToRender: ContractSection[] = (() => {
    // First try database instance sections (for saved/signed contracts)
    if (dbInstance?.sections && Array.isArray(dbInstance.sections) && dbInstance.sections.length > 0) {
      return dbInstance.sections;
    }
    // Then fall back to template sections
    if (template?.sections && Array.isArray(template.sections) && template.sections.length > 0) {
      return template.sections;
    }
    // Log if no sections found
    console.warn('[ContractPreview] No sections available', {
      candidateId,
      dbInstanceExists: !!dbInstance,
      dbInstanceSectionsCount: dbInstance?.sections?.length ?? 0,
      templateExists: !!template,
      templateSectionsCount: template?.sections?.length ?? 0
    });
    return [];
  })();

  // Sort sections by order
  const sortedSections = [...sectionsToRender].sort((a, b) => {
    if (a.order === 'title') {
return -1;
}
    if (b.order === 'title') {
return 1;
}
    return a.order - b.order;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Template Editor Modal */}
      {isEditorOpen && template && (
        <ContractTemplateEditor
          template={template}
          variableData={variableData}
          onClose={() => setIsEditorOpen(false)}
          onSave={(updatedTemplate) => {
            saveTemplate(updatedTemplate);
            setIsEditorOpen(false);
          }}
        />
      )}

      {/* Header Actions */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div className="dw-sec-title" style={{ marginBottom: 4 }}>Contract Preview</div>
          <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>
            Review the contract with data from the LO Data tab
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <PermissionGate roles={['admin']}>
            <button type="button" className="btn-sm" onClick={() => setIsEditorOpen(true)}>
              <Edit />
              Edit Template
            </button>
          </PermissionGate>
          <button type="button" className="btn-sm" onClick={handleExportPDF}>
            <Download />
            Export PDF
          </button>
        </div>
      </div>

      {/* Missing Data Warning */}
      {(!formData.firstName || !formData.lastName || !formData.nmlsNumber) && (
        <div style={{ background: '#F7F0DC', border: '1px solid #E4D2A0', borderRadius: 11, padding: '13px 14px' }}>
          <p style={{ fontSize: 12.5, color: '#7E6426', margin: 0 }}>
            Some required fields are missing. Fill in the LO Data tab to see complete contract details.
          </p>
        </div>
      )}

      {/* Contract Preview Card */}
      <div className="panel" style={{ padding: 32 }}>
        <div className="contract-document max-w-4xl mx-auto">
          {sortedSections.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--lw-muted)' }}>
              <FileText size={48} style={{ margin: '0 auto 16px', display: 'block', color: 'var(--line-strong, #CFC8B6)' }} />
              <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>No contract sections available</p>
              <p style={{ fontSize: 13, marginTop: 8 }}>
                {dbInstanceLoading ? 'Loading contract data...' : 'Contract sections could not be loaded.'}
              </p>
            </div>
          ) : (
            sortedSections.map((section) => (
              <SectionRenderer
                key={section.id}
                section={section}
                variableData={variableData}
                checkboxStates={checkboxStates}
                onCheckboxChange={handleCheckboxChange}
                readOnly={!candidateId} // Only allow editing if we have a candidate ID
                interpolate={interpolate}
              />
            ))
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--lw-muted)' }}>
        <p style={{ margin: 0 }}>Contract Template Version {template.version}</p>
        <p style={{ fontSize: 11.5, marginTop: 4 }}>
          Last updated: {new Date(template.updatedAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};
